import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import {
  componentsPropType,
  stylesPropType,
  vmPropTypes
} from '../../../global-prop-types';
import ComponentsArrRenderer from '../../core/ComponentsArrRenderer';
import moduleStyles from './styles.module.css';
import useTheme from '../../../global-hooks/useTheme';

const breakpoints = {
  xxl: 1440,
  xl: 1200,
  lg: 992,
  md: 768,
  sm: 576
};

const allowedValues = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const validateAndFillBreakpoints = (itemWidthBreakpoints, themeBreakpoints) => {
  let lastValidValue = 12;
  const orderedBreakpoints = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'];

  return orderedBreakpoints.reduce((acc, key) => {
    const value = itemWidthBreakpoints[key];
    if (allowedValues.includes(value)) {
      acc[key] = value || lastValidValue;
      if (value) lastValidValue = value;
    } else {
      acc[key] = lastValidValue;
    }
    return acc;
  }, {});
};

const getWidth = (size) => (100 / 12) * size;

function GridItem(props) {
  const {
    styles,
    variant,
    xxl,
    xl,
    lg,
    md,
    sm,
    xs,
    startTimeGridItem,
    endTimeGridItem,
    gridItemInlineGap = 0,
    gridItemVerticalGap = 0,
    ...restProps
  } = props;

  const theme = useTheme();
  const [width, setWidth] = useState('100%');
  const validatedBreakpoints = useMemo(
    () => validateAndFillBreakpoints(
      {
        xxl, xl, lg, md, sm, xs
      },
      theme.breakpoints.values
    ),
    [xxl, xl, lg, md, sm, xs, theme]
  );

  const updateWidth = () => {
    let newWidth = '100%';

    for (const key of Object.keys(theme.breakpoints.values)) {
      if (window.matchMedia(`(min-width: ${breakpoints[key]}px)`).matches) {
        newWidth = `${getWidth(validatedBreakpoints[key])}%`;
      }
    }

    setWidth(newWidth);
  };
  const isDateValid = (dateStr) => dateStr instanceof Date && !isNaN(dateStr);

  const showByDateTime = () => {
    const now = Date.now();

    if (isDateValid(startTimeGridItem) && isDateValid(endTimeGridItem)) {
      if (now >= startTimeGridItem && now <= endTimeGridItem) {
        return true;
      }

      return false;
    }

    return true;
  };
  useEffect(() => {
    updateWidth();

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [validatedBreakpoints]);

  return (
    showByDateTime() && (
      <div
        className={`${moduleStyles.gridItem} GridItem ${variant || ''}`}
        style={{
          flex: `0 0 ${width}`,
          display: 'flex',
          flexDirection: 'column',
          maxWidth: width,
          paddingInline: gridItemInlineGap / 2,
          paddingBottom: gridItemVerticalGap,
          ...styles?.wrapper,
        }}
      >
        <ComponentsArrRenderer {...restProps} />
      </div>
    )
  );
}

GridItem.propTypes = {
  styles: stylesPropType,
  xxl: PropTypes.number,
  xl: PropTypes.number,
  lg: PropTypes.number,
  md: PropTypes.number,
  sm: PropTypes.number,
  xs: PropTypes.number,
  startTimeGridItem: PropTypes.object,
  endTimeGridItem: PropTypes.object,
  gridItemInlineGap: PropTypes.number,
  gridItemVerticalGap: PropTypes.number,
  variant: PropTypes.string,
  components: componentsPropType
};

GridItem.defaultProps = {
  variant: '',
  styles: {
    wrapper: {}
  },
  xxl: 0,
  xl: 0,
  lg: 0,
  md: 0,
  sm: 0,
  xs: 0,
  startTimeGridItem: '',
  endTimeGridItem: '',
  components: []
};

GridItem.vmPropTypes = {
  styles: { wrapper: vmPropTypes.styles },
  xxl: vmPropTypes.number,
  xl: vmPropTypes.number,
  lg: vmPropTypes.number,
  md: vmPropTypes.number,
  sm: vmPropTypes.number,
  xs: vmPropTypes.number,
  startTimeGridItem: vmPropTypes.timestamp,
  endTimeGridItem: vmPropTypes.timestamp,
  variant: vmPropTypes.className,
  components: vmPropTypes.components
};

GridItem.processPropVmTypes = (props) => {
  const { styles, variant, ...restProps } = props;

  return ComponentsArrRenderer.processPropVmTypes(restProps);
};

export default GridItem;
